import equal from "fast-deep-equal";
import {
  CheckParameterFeasibilityRequest,
  ParameterConflict,
} from "src/store/api/generatedApi";

export interface CompletedFeasibilityCheck {
  request: CheckParameterFeasibilityRequest;
  conflicts: ParameterConflict[];
}

const groupRequests = (request: CheckParameterFeasibilityRequest) => {
  const groups = new Map<number, CheckParameterFeasibilityRequest>();
  const forGroup = (chefGroupId: number) => {
    const existing = groups.get(chefGroupId);
    if (existing) return existing;
    const candidate: CheckParameterFeasibilityRequest = {
      material_physics_overrides: null,
      material_chemistry_overrides: null,
      material_limits: request.material_limits == null ? null : [],
      target_baskets: request.target_baskets == null ? null : [],
    };
    groups.set(chefGroupId, candidate);
    return candidate;
  };
  for (const limit of request.material_limits ?? []) {
    forGroup(limit.chef_group_id).material_limits?.push(limit);
  }
  for (const basket of request.target_baskets ?? []) {
    forGroup(basket.chef_group_id).target_baskets?.push(basket);
  }
  return groups;
};

export const prepareFeasibilityCheck = ({
  request,
  completed,
}: {
  request: CheckParameterFeasibilityRequest;
  completed: CompletedFeasibilityCheck | null;
}): {
  request: CheckParameterFeasibilityRequest | null;
  retainedConflicts: ParameterConflict[];
} => {
  const groups = groupRequests(request);
  if (
    completed === null ||
    request.material_physics_overrides != null ||
    completed.request.material_physics_overrides != null ||
    request.material_chemistry_overrides != null ||
    completed.request.material_chemistry_overrides != null ||
    groups.size === 0 ||
    request.material_limits == null
  ) {
    return { request, retainedConflicts: [] };
  }
  const previousGroups = groupRequests(completed.request);
  const changedIds = new Set(
    [...groups]
      .filter(([id, candidate]) => !equal(candidate, previousGroups.get(id)))
      .map(([id]) => id)
  );
  return {
    request:
      changedIds.size === 0
        ? null
        : {
            material_physics_overrides: null,
            material_chemistry_overrides: null,
            material_limits: request.material_limits.filter((limit) =>
              changedIds.has(limit.chef_group_id)
            ),
            target_baskets:
              request.target_baskets?.filter((basket) =>
                changedIds.has(basket.chef_group_id)
              ) ?? null,
          },
    retainedConflicts: completed.conflicts.filter(
      (conflict) =>
        groups.has(conflict.chef_group_id) &&
        !changedIds.has(conflict.chef_group_id)
    ),
  };
};
